/*
 * UI translation dictionaries — English and Greek.
 * Client-specific strings live here; rule, section, and preference display
 * metadata comes from the backend Greek editor catalog.
 *
 * Ported verbatim from the original translations.js (no string changes).
 */

import type { Lang } from "../state/types";

export const translations = {
  en: {
    appTitle: "Greek Literature Editor",
    langToggle: "Ελληνικά",
    tabBatch: "Apply",
    tabLive: "Live",
    tabSelection: "Confusion",
    tabRules: "Rules",
    tabSettings: "Account",
    authEmailTitle: "Sign in to your account",
    authEmailSubtitle: "We'll send a one-time code to your email.",
    authEmailLabel: "Email address",
    authEmailPlaceholder: "you@example.com",
    authEmailBtn: "Send Code",
    authEmailSending: "Sending…",
    authPasskeyBtn: "Sign in with a passkey",
    authPasskeySigningIn: "Signing in with passkey…",
    authPasskeyOr: "or",
    authPasskeyHint: "Fast, passwordless sign-in with your device or security key.",
    authPasskeyNotSupported:
      "Passkeys are not supported in this Office host or browser. Please sign in with email.",
    authPasskeyCancelled: "Passkey sign-in was cancelled. You can try again or sign in with email.",
    authPasskeySecurityRestricted:
      "Passkey authentication is restricted in this window. Please sign in with email.",
    authPasskeyFeatureDisabled:
      "Passkey authentication is currently disabled. Please sign in with email.",
    authPasskeyChallengeExpired: "Passkey challenge expired or invalid. Please try again.",
    authPasskeyFailed: "Passkey verification failed. Please try again or sign in with email.",
    authDevPersonaHint: "Development personas — local backend only.",
    authDevPersonas: {
      author: "Author",
      editor: "Editor",
      publisher: "Publisher",
      reviewer: "Reviewer",
    },
    authOtpTitle: "Check your inbox",
    authOtpSubtitle: "Enter the 6-digit code we sent you.",
    authOtpLabel: "One-time code",
    authOtpPlaceholder: "123456",
    authOtpBtn: "Sign In",
    authOtpVerifying: "Verifying…",
    authOtpBack: "← Use a different email",
    authOtpTrustLabel: "Trust this device",
    authOtpTrustHint: "Skip the code next time on this computer.",
    authOtpDeviceLabel: "Device name",
    authOtpDevicePlaceholder: "e.g. My Work Mac",
    authTrustedSigningIn: "Signing in…",
    tokenPickerNoPlan: "Free",
    planFamilies: {
      author: "Author",
      editor: "Editor",
      combined: "Combined",
      business: "Business",
    },
    planTiers: {
      standard: "Standard",
      pro: "Pro",
    },
    loggedInTitle: "Signed in",
    loggedInPlan: "Current plan:",
    loggedInRenews: "Renews on:",
    loggedInUpgrade: "Upgrade plan",
    loggedInPortalLinks: "Manage account readiness in the portal.",
    loggedInBillingLegal: "Billing & legal",
    loggedInTerms: "Terms",
    loggedInPrivacy: "Privacy",
    loggedInLogout: "Sign out",
    rulesInfo:
      "Choose which proofreading and style rules should be used. This selection drives every other flow — Batch Clean, Live, Review, and the Confusion Assistant.",
    rulesTitle: "Rules to Apply",
    selectAll: "Select All",
    deselectAll: "Deselect All",
    rulePresetsTitle: "Built-in presets",
    rulePresetsHint: "Choose a setup, then adjust individual rules.",
    selectedRulesCount: "{count} rules",
    optionalRule: "Optional",
    unsafeRuleLabel: "Review",
    unsafeRule: "Review recommended — may produce unwanted changes",
    safeRule: "Safe",
    dependsOn: "Requires",
    enabledRule: "Enabled",
    disabledRule: "Off",
    rulePresets: {
      conservative: {
        title: "Conservative proofreading",
        description: "Safe cleanup that keeps the author's voice and skips ambiguity checks.",
      },
      orthography: {
        title: "Orthography only",
        description: "Spelling and common word-form cleanup without broader style changes.",
      },
      literary: {
        title: "Literary flow cleanup",
        description: "Style, contractions, punctuation, and spacing rules for prose.",
      },
      dialogue: {
        title: "Dialogue punctuation pass",
        description: "Quotation, spacing, ellipsis, comma, and period-focused cleanup.",
      },
      authorVoice: {
        title: "Author voice preservation",
        description: "A cautious spelling-focused profile for distinctive prose.",
      },
    },
    batchInfo:
      "Applies every selected rule across the whole document or just your selection in a single pass and writes the corrected text straight back — use this for a fast bulk cleanup without reviewing each change individually.",
    reviewModeLabel: "How should changes be shown?",
    reviewModeTracked: "Track Changes",
    reviewModeClean: "Overwrite",
    applyBtn: "Apply to Entire Document",
    applySelectionBtn: "Apply to Selection",
    applyReadyStatus: "Ready to apply corrections.",
    statusReading: "Reading document content…",
    statusReadingSelection: "Reading selected text…",
    statusProcessing: "Applying corrections via backend…",
    statusProcessingSelection: "Applying selected rules to selection…",
    statusWriting: "Writing corrected document…",
    statusWritingSelection: "Writing corrected selection…",
    statusDone: "Corrections applied successfully!",
    statusError: "An error occurred:",
    noRulesSelected: "Please select at least one rule first.",
    emptySelection: "The selected text is empty.",
    occurrenceNotFound:
      "Couldn't locate this word in the selection — it may have changed. Re-scan the selection and try again.",
    selectionApplied: "Selected text updated successfully.",
    trackedSelectionUnsupported:
      "Tracked changes for selections require WordApi 1.4. Switch to direct edit or use the whole-document tracked mode.",
    commentUnsupported:
      "Adding Word comments requires WordApi 1.4. Use the whole-document apply flow to let the backend write comments into the DOCX.",
    scanBtn: "Scan Selection",
    statusScanning: "Scanning selection…",
    noIssues: "No Greek confusion cases found in the selection.",
    noConfusionRulesSelected: "Select at least one confusion rule before scanning.",
    noConfusionRulesAvailable: "No confusion rules are available.",
    confusionRulesTitle: "Confusion rules in this scan",
    confusionRulesHint:
      "Only enabled confusion rules are scanned here. Turn them on or off before scanning the selection.",
    promptSelection: "Select a paragraph in Word, then click 'Scan Selection'.",
    cardSentence: "Affected sentence:",
    cardOriginal: "Original:",
    cardSuggested: "Suggested fix:",
    cardRule: "Rule:",
    acceptBtn: "Apply Fix",
    addCommentBtn: "Add Comment",
    skipBtn: "Skip",
    cancel: "Cancel",
    confusionCounter: "Confusion {current} of {total}",
    unauthWarning: "Please sign in from the Account tab first.",
    noRulesWarning: "Please select which rules you want to apply in the 'Rules' tab first.",
    rulesDependencyInfo: "All correction flows depend on the rules configured in the 'Rules' tab.",
    confusionAssistantInfo:
      "This assistant scans and applies only the confusion rules to your selection.",
    wordsRemainingLabel: "Words remaining: {value}",
    wordsConsumedLabel: "Words consumed: {value}",
    wordsUsageLabel: "Usage: {value}%",
    ruleFallbackDescription: "Applies an editorial cleanup rule from the Greek editor catalog.",
    invalidApplyTextResponse: "The correction service did not return corrected text.",
    liveInfo:
      "Checks the paragraph you're writing as soon as you pause typing and flags suggestions and repeated words inline — nothing changes in your document until you accept a suggestion.",
    liveHeader: "Live Feedback",
    liveToggleLabel: "Live paragraph editorial feedback",
    liveToggleHint: "Scans the current paragraph after typing pauses.",
    livePrompt: "Turn on live feedback and place the cursor inside a paragraph.",
    liveIdle: "Live feedback is on. Start writing or move the cursor into a paragraph.",
    liveWaiting: "Waiting for typing to pause…",
    liveScanning: "Scanning current paragraph…",
    liveNoIssues: "No live suggestions found for the current paragraph.",
    liveNoEligibleRules: "Select at least one editor rule in the Rules tab.",
    liveUnsupported: "This Word client cannot read the current paragraph for live feedback.",
    liveStaleSuggestion: "The paragraph changed. Rescanning before applying this suggestion.",
    liveCounter: "Suggestion {current} of {total}",
    liveRulesHeader: "What should live feedback scan?",
    liveRepetitionLabel: "Detect word repetitions",
    liveRepetitionHint: "Flags repeated words in the current paragraph.",
    liveRepWindowLabel: "Margin of words",
    liveRepWindowHint: "Max word distance between occurrences (1–250).",
    liveRepFrequencyLabel: "Frequency",
    liveRepFrequencyHint: "Min occurrences to report (2–20).",
    liveRepetitionsHeader: "Word repetitions",
    liveNoRepetitions: "No word repetitions found in the current paragraph.",
    liveRepOccurrences: "{count} occurrences",
    liveRepSuggestions: "Try instead:",
    liveRepForms: "Forms:",
    askAiForHelp: "Ask AI for help",
    aiSuggestionsLoading: "Getting suggestions…",
    aiSuggestionsError: "Could not get suggestions.",
    aiSuggestionsLabel: "AI suggests:",
    aiQuotaRemaining: "{count} AI requests left today",
    aiQuotaExhausted: "Daily AI limit reached",
    severityLow: "Low",
    severityMedium: "Medium",
    severityHigh: "High",
    tabSuggestions: "Review",
    suggestionsInfo:
      "Scans the whole document or just your selection and walks you through every proposed correction one at a time, so you can accept, skip, or apply all instances of a rule before anything changes.",
    scanSuggestionsBtn: "Scan Document",
    scanSelectionSuggestionsBtn: "Scan Selection",
    promptSuggestions:
      "Click 'Scan Document' to review proposed editorial corrections for the whole document, or select some text first and click 'Scan Selection' to review just that part.",
    suggestionsHeader: "Review Suggestions",
    suggestionsCounter: "Suggestion {current} of {total}",
    acceptAllRuleBtn: "Apply all of this rule",
    noSuggestions: "No suggestions found. The document conforms to selected rules.",
    reportFalsePositive: "Report as false positive",
    reportFalsePositiveTitle: "Report false positive",
    reportFalsePositiveDescription:
      "Help us improve by reporting when a rule incorrectly changed text.",
    reportFalsePositiveFeedbackLabel: "Additional feedback (optional)",
    reportFalsePositiveFeedbackPlaceholder:
      "Explain what didn't work well and how it should have been handled...",
    reportFalsePositiveSend: "Send report",
    reportFalsePositiveSending: "Sending...",
    reportFalsePositiveSuccess: "Report sent successfully. Thank you!",
    reportFalsePositiveError: "Failed to send report. Please try again.",
    reportFalsePositiveCancel: "Cancel",
    recentlyChangedTitle: "Recently added or updated",
    badgeNew: "New",
    badgeUpdated: "Updated",
    recentSelectionsTitle: "Recent selections",
    recentSelectionLabel: "{count} rules",
    managedSectionTitle: "Managed presets",
    personalSectionTitle: "My presets",
    managedBadge: "Managed",
    duplicatePreset: "Duplicate to my presets",
    duplicatingPreset: "Duplicating…",
    quotaExceededTitle: "You've reached your plan's limit",
    quotaExceededMessage: "Upgrade for more scans and corrections.",
    serviceForbiddenMessage: "This feature requires a higher plan. Upgrade to continue.",
    quotaExceededUpgrade: "Upgrade plan",
    karlName: "Karl",
    karlRole: "editor's assistant",
    karlAsk: "Ask Karl",
    karlAsking: "Karl is thinking…",
    karlAskRule: "Ask Karl about this rule ({count})",
    karlAskAll: "Ask Karl about all findings ({count})",
    karlBulkPanelLabel: "Karl's estimate",
    karlBulkTitle: "Karl's estimate for {scope}",
    karlBulkScopeAll: "all findings",
    karlBulkEstimating: "Karl is checking how many assists this will use…",
    karlBulkEstimate:
      "This will use {count} assists; you have {today} left today and {month} this month.",
    karlBulkEstimateOnly: "This will use {count} assists.",
    karlBulkEstimateUnlimited: "This will use {count} assists. Your plan has no limit.",
    karlBulkEstimateMonthOnly: "This will use {count} assists; you have {month} left this month.",
    karlBulkCachedNote: "{count} findings are already answered and cost no assists.",
    karlBulkShortfall:
      "This needs {count} assists but only {maximum} are left. Karl can answer the first {maximum} in reading order.",
    karlBulkAnswerAll: "Ask Karl about {count} findings",
    karlBulkAnswerFirst: "Answer the first {count}",
    karlBulkShowAnswers: "Show Karl's answers",
    karlBulkCancel: "Cancel",
    karlBulkAnswered: "{count} answered",
    karlBulkCached: "{count} already answered",
    karlBulkUnanswered: "{count} not answered (no assists left)",
    karlBulkFailed: "{count} failed",
    karlBulkLimitStopped:
      "Karl stopped at the number of findings you approved. Ask again for the rest.",
    karlKeepBadge: "Keep «{word}» · {confidence}",
    karlChangeBadge: "Change to «{word}» · {confidence}",
    karlConfidenceSure: "Sure",
    karlConfidenceLikely: "Likely",
    karlConfidenceUncertain: "Uncertain",
    karlStyleChoice: "Often a style choice.",
    karlUnsupported: "I haven't been trained on this rule yet.",
    karlUnavailable: "Karl can't answer right now.",
    karlAssistsUsed: "{used} used today · {today} left today · {month} left this month",
    karlAssistsMonthOnly: "{used} used this month · {month} left this month",
    karlAssistsUnlimited: "Unlimited assists",
    karlAcceptedWithKarl: "Accepted with Karl",
    karlUndo: "Undo",
    karlApplyHint:
      "Karl is sure about the findings below. You can apply his Sure verdicts and skip them.",
    karlApplyAction: "Apply Karl's Sure verdicts ({count})",
    karlApplyConfirmLabel: "Confirm the bulk apply",
    karlApplyConfirm:
      "This resolves {findings} findings Karl is sure about, {changes} of them with a change. Each one can be undone.",
    karlApplyConfirmAction: "Apply {count}",
    karlApplyCancel: "Cancel",
    karlOverviewTitle: "Karl's results",
    karlOverviewHint: "Counts cover the findings in this scan.",
    karlOverviewClearFilter: "Show all",
    karlOverviewNoAnswers:
      "Ask Karl about a finding to see his results per rule and filter the cases here.",
    karlOverviewRuleCount: "{answered} of {total} answered",
    karlCountAnswered: "Answered",
    karlCountKeep: "Keep",
    karlCountChange: "Change",
    karlCountSure: "Sure",
    karlCountLikely: "Likely",
    karlCountUncertain: "Uncertain",
    karlFilterLabel: "Only Likely and Uncertain",
    karlFilterHint: "Skip the cases Karl is sure about.",
    karlFilteredEmpty: "No cases match this filter.",
    karlAppliedTitle: "Applied with Karl",
    karlAssistsExhaustedToday:
      "Karl can't answer right now: no assists left today. They reset at midnight UTC.",
    karlAssistsExhaustedCycle:
      "Karl can't answer right now: no assists left this month. They reset at the start of next month.",
    karlAssistsExhausted: "Karl can't answer right now: no assists left.",
    karlDisclaimer:
      "Karl's verdicts are advice, not a guarantee. Your review is the final quality stamp.",
    karlConsentTitle: "Before you ask Karl",
    karlConsentDisclaimer:
      "Karl is a specialised tool. His verdicts are not a guarantee — your review is the final quality stamp.",
    karlConsentPrivacyNote:
      "Karl answers with an external model. Only the sentences of the findings you ask about are sent to the provider, and it keeps no copy of them.",
    karlConsentPrivacyLink: "Read the privacy policy",
    karlConsentAccept: "Accept and continue",
    karlConsentCancel: "Not now",
    karlConsentFailed: "We couldn't save your acceptance. Please try again.",
    karlConsentAccountRequired:
      "Karl needs a signed-in account to record your acceptance. Sign in from the Account tab and try again.",
    karlConsentGoToAccount: "Go to Account",
  },
  el: {
    appTitle: "Διορθωτής Ελληνικών Κειμένων",
    langToggle: "English",
    tabBatch: "Εφαρμογή",
    tabLive: "Ζωντανά",
    tabSelection: "Σύγχυσης",
    tabRules: "Κανόνες",
    tabSettings: "Λογαριασμός",
    authEmailTitle: "Σύνδεση στο λογαριασμό σας",
    authEmailSubtitle: "Θα σας στείλουμε έναν κωδικό μίας χρήσης.",
    authEmailLabel: "Διεύθυνση email",
    authEmailPlaceholder: "εσείς@παράδειγμα.com",
    authEmailBtn: "Αποστολή Κωδικού",
    authEmailSending: "Αποστολή…",
    authPasskeyBtn: "Σύνδεση με passkey",
    authPasskeySigningIn: "Σύνδεση με passkey…",
    authPasskeyOr: "ή",
    authPasskeyHint: "Γρήγορη σύνδεση χωρίς κωδικό με τη συσκευή ή το κλειδί ασφαλείας σας.",
    authPasskeyNotSupported:
      "Τα passkeys δεν υποστηρίζονται σε αυτό το περιβάλλον Office. Συνδεθείτε με email.",
    authPasskeyCancelled:
      "Η σύνδεση με passkey ακυρώθηκε. Μπορείτε να δοκιμάσετε ξανά ή να συνδεθείτε με email.",
    authPasskeySecurityRestricted:
      "Η σύνδεση με passkey δεν επιτρέπεται σε αυτό το παράθυρο. Συνδεθείτε με email.",
    authPasskeyFeatureDisabled: "Η σύνδεση με passkey είναι απενεργοποιημένη. Συνδεθείτε με email.",
    authPasskeyChallengeExpired: "Η πρόκληση passkey έληξε ή δεν είναι έγκυρη. Δοκιμάστε ξανά.",
    authPasskeyFailed: "Η επαλήθευση passkey απέτυχε. Δοκιμάστε ξανά ή συνδεθείτε με email.",
    authDevPersonaHint: "Προφίλ ανάπτυξης — μόνο για τοπικό backend.",
    authDevPersonas: {
      author: "Συγγραφέας",
      editor: "Επιμελητής",
      publisher: "Εκδότης",
      reviewer: "Αξιολογητής",
    },
    authOtpTitle: "Ελέγξτε το email σας",
    authOtpSubtitle: "Εισάγετε τον 6-ψήφιο κωδικό που σας στείλαμε.",
    authOtpLabel: "Κωδικός μίας χρήσης",
    authOtpPlaceholder: "123456",
    authOtpBtn: "Σύνδεση",
    authOtpVerifying: "Επαλήθευση…",
    authOtpBack: "← Χρησιμοποιήστε διαφορετικό email",
    authOtpTrustLabel: "Εμπιστευτείτε αυτή τη συσκευή",
    authOtpTrustHint: "Παρακάμψτε τον κωδικό την επόμενη φορά σε αυτόν τον υπολογιστή.",
    authOtpDeviceLabel: "Όνομα συσκευής",
    authOtpDevicePlaceholder: "π.χ. Ο εργασιακός μου Mac",
    authTrustedSigningIn: "Σύνδεση…",
    tokenPickerNoPlan: "Δωρεάν",
    planFamilies: {
      author: "Συγγραφέας",
      editor: "Επιμελητής",
      combined: "Συνδυαστικό",
      business: "Επιχείρηση",
    },
    planTiers: {
      standard: "Βασικό",
      pro: "Pro",
    },
    loggedInTitle: "Συνδεδεμένος",
    loggedInPlan: "Τρέχον πρόγραμμα:",
    loggedInRenews: "Ανανέωση στις:",
    loggedInUpgrade: "Αναβάθμιση προγράμματος",
    loggedInPortalLinks: "Διαχειριστείτε την ετοιμότητα λογαριασμού στο portal.",
    loggedInBillingLegal: "Χρέωση & νομικά",
    loggedInTerms: "Όροι",
    loggedInPrivacy: "Απόρρητο",
    loggedInLogout: "Αποσύνδεση",
    rulesInfo:
      "Επιλέξτε ποιους κανόνες ορθογραφίας και ύφους θα χρησιμοποιεί η εφαρμογή. Αυτή η επιλογή καθορίζει όλες τις υπόλοιπες ροές — Μαζική Διόρθωση, Ζωντανή Ανατροφοδότηση, Προτάσεις και Βοηθό Σύγχυσης.",
    rulesTitle: "Κανόνες προς Εφαρμογή",
    selectAll: "Επιλογή Όλων",
    deselectAll: "Αποεπιλογή Όλων",
    rulePresetsTitle: "Έτοιμα presets",
    rulePresetsHint: "Επιλέξτε μια βάση και μετά προσαρμόστε μεμονωμένους κανόνες.",
    selectedRulesCount: "{count} κανόνες",
    optionalRule: "Προαιρετικός",
    unsafeRuleLabel: "Έλεγχος",
    unsafeRule: "Συνιστάται έλεγχος — ενδέχεται ανεπιθύμητες αλλαγές",
    safeRule: "Ασφαλής",
    dependsOn: "Απαιτεί",
    enabledRule: "Ενεργός",
    disabledRule: "Ανενεργός",
    rulePresets: {
      conservative: {
        title: "Συντηρητική διόρθωση",
        description:
          "Ασφαλής καθαρισμός που διατηρεί τη φωνή του συγγραφέα και παραλείπει αμφίσημες περιπτώσεις.",
      },
      orthography: {
        title: "Μόνο ορθογραφία",
        description:
          "Διόρθωση ορθογραφίας και συνηθισμένων τύπων χωρίς ευρύτερες υφολογικές αλλαγές.",
      },
      literary: {
        title: "Λογοτεχνική ροή",
        description: "Κανόνες ύφους, συντμήσεων, στίξης και αποστάσεων για πεζό κείμενο.",
      },
      dialogue: {
        title: "Στίξη διαλόγων",
        description: "Καθαρισμός εισαγωγικών, αποστάσεων, αποσιωπητικών, κομμάτων και τελειών.",
      },
      authorVoice: {
        title: "Διατήρηση φωνής συγγραφέα",
        description: "Προσεκτικό ορθογραφικό προφίλ για ιδιαίτερο ύφος γραφής.",
      },
    },
    batchInfo:
      "Εφαρμόζει όλους τους επιλεγμένους κανόνες σε ολόκληρο το έγγραφο ή μόνο στην επιλογή σας με ένα πέρασμα και γράφει αμέσως πίσω το διορθωμένο κείμενο — χρησιμοποιήστε το για γρήγορο μαζικό καθαρισμό χωρίς να ελέγξετε κάθε αλλαγή ξεχωριστά.",
    reviewModeLabel: "Εμφάνιση αλλαγών ως:",
    reviewModeTracked: "Track Changes",
    reviewModeClean: "Αντικατάσταση",
    applyBtn: "Εφαρμογή σε Όλο το Έγγραφο",
    applySelectionBtn: "Εφαρμογή στην Επιλογή",
    applyReadyStatus: "Έτοιμο για εφαρμογή διορθώσεων.",
    statusReading: "Ανάγνωση εγγράφου…",
    statusReadingSelection: "Ανάγνωση επιλεγμένου κειμένου…",
    statusProcessing: "Εφαρμογή διορθώσεων μέσω backend…",
    statusProcessingSelection: "Εφαρμογή επιλεγμένων κανόνων στην επιλογή…",
    statusWriting: "Εγγραφή διορθωμένου εγγράφου…",
    statusWritingSelection: "Εγγραφή διορθωμένης επιλογής…",
    statusDone: "Οι διορθώσεις εφαρμόστηκαν επιτυχώς!",
    statusError: "Παρουσιάστηκε σφάλμα:",
    noRulesSelected: "Παρακαλώ επιλέξτε τουλάχιστον έναν κανόνα πρώτα.",
    emptySelection: "Το επιλεγμένο κείμενο είναι κενό.",
    occurrenceNotFound:
      "Δεν βρέθηκε αυτή η λέξη στην επιλογή — ίσως άλλαξε. Κάντε ξανά έλεγχο της επιλογής και δοκιμάστε πάλι.",
    selectionApplied: "Το επιλεγμένο κείμενο ενημερώθηκε επιτυχώς.",
    trackedSelectionUnsupported:
      "Η καταγραφή αλλαγών σε επιλογή απαιτεί WordApi 1.4. Επιλέξτε απευθείας αντικατάσταση ή χρησιμοποιήστε την καταγραφή αλλαγών για όλο το έγγραφο.",
    commentUnsupported:
      "Η προσθήκη σχολίων στο Word απαιτεί WordApi 1.4. Χρησιμοποιήστε την εφαρμογή σε όλο το έγγραφο ώστε το backend να γράψει τα σχόλια στο DOCX.",
    scanBtn: "Έλεγχος Επιλογής",
    statusScanning: "Έλεγχος επιλεγμένου κειμένου…",
    noIssues: "Δεν βρέθηκαν διφορούμενες ελληνικές λέξεις.",
    noConfusionRulesSelected: "Επιλέξτε τουλάχιστον έναν κανόνα σύγχυσης πριν από τον έλεγχο.",
    noConfusionRulesAvailable: "Δεν υπάρχουν διαθέσιμοι κανόνες σύγχυσης.",
    confusionRulesTitle: "Κανόνες σύγχυσης σε αυτόν τον έλεγχο",
    confusionRulesHint:
      "Εδώ ελέγχονται μόνο οι ενεργοί κανόνες σύγχυσης. Ενεργοποιήστε ή απενεργοποιήστε τους πριν από τον έλεγχο της επιλογής.",
    promptSelection: "Επιλέξτε μια παράγραφο στο Word, μετά κάντε κλικ στο 'Έλεγχος Επιλογής'.",
    cardSentence: "Επηρεαζόμενη πρόταση:",
    cardOriginal: "Αρχικό:",
    cardSuggested: "Προτεινόμενο:",
    cardRule: "Κανόνας:",
    acceptBtn: "Εφαρμογή Διόρθωσης",
    addCommentBtn: "Προσθήκη σχολίου",
    skipBtn: "Παράκαμψη",
    cancel: "Ακύρωση",
    confusionCounter: "Σύγχυση {current} από {total}",
    unauthWarning: "Παρακαλώ συνδεθείτε πρώτα από την καρτέλα Λογαριασμός.",
    noRulesWarning:
      "Παρακαλώ επιλέξτε πρώτα ποιους κανόνες θέλετε να εφαρμόσετε στην καρτέλα 'Κανόνες'.",
    rulesDependencyInfo:
      "Όλες οι ροές διόρθωσης εξαρτώνται από τους κανόνες που έχουν επιλεγεί στην καρτέλα 'Κανόνες'.",
    confusionAssistantInfo:
      "Αυτός ο βοηθός ελέγχει και εφαρμόζει μόνο τους κανόνες σύγχυσης στην επιλογή σας.",
    wordsRemainingLabel: "Υπόλοιπο λέξεων: {value}",
    wordsConsumedLabel: "Κατανάλωση: {value}",
    wordsUsageLabel: "Χρήση: {value}%",
    ruleFallbackDescription: "Εφαρμόζει κανόνα επιμέλειας από τον κατάλογο του ελληνικού διορθωτή.",
    invalidApplyTextResponse: "Η υπηρεσία διόρθωσης δεν επέστρεψε διορθωμένο κείμενο.",
    liveInfo:
      "Ελέγχει την παράγραφο που γράφετε μόλις σταματήσετε να πληκτρολογείτε και επισημαίνει προτάσεις και επαναλαμβανόμενες λέξεις απευθείας στο κείμενο — τίποτα δεν αλλάζει στο έγγραφό σας μέχρι να αποδεχτείτε μια πρόταση.",
    liveHeader: "Ζωντανή Ανατροφοδότηση",
    liveToggleLabel: "Ζωντανή συμβουλευτική επεξεργασία παραγράφου",
    liveToggleHint: "Ελέγχει την τρέχουσα παράγραφο όταν σταματάτε να πληκτρολογείτε.",
    livePrompt: "Ενεργοποιήστε τον ζωντανό έλεγχο και τοποθετήστε τον κέρσορα σε μια παράγραφο.",
    liveIdle:
      "Ο ζωντανός έλεγχος είναι ενεργός. Γράψτε ή μετακινήστε τον κέρσορα σε μια παράγραφο.",
    liveWaiting: "Αναμονή μέχρι να σταματήσει η πληκτρολόγηση…",
    liveScanning: "Έλεγχος τρέχουσας παραγράφου…",
    liveNoIssues: "Δεν βρέθηκαν ζωντανές προτάσεις για την τρέχουσα παράγραφο.",
    liveNoEligibleRules: "Επιλέξτε τουλάχιστον έναν κανόνα επιμέλειας στην καρτέλα Κανόνες.",
    liveUnsupported:
      "Αυτή η έκδοση του Word δεν μπορεί να διαβάσει την τρέχουσα παράγραφο για ζωντανό έλεγχο.",
    liveStaleSuggestion: "Η παράγραφος άλλαξε. Γίνεται νέος έλεγχος πριν εφαρμοστεί η πρόταση.",
    liveCounter: "Πρόταση {current} από {total}",
    liveRulesHeader: "Τι να ελέγχει ο ζωντανός έλεγχος;",
    liveRepetitionLabel: "Εντοπισμός επαναλήψεων λέξεων",
    liveRepetitionHint: "Επισημαίνει επαναλαμβανόμενες λέξεις στην τρέχουσα παράγραφο.",
    liveRepWindowLabel: "Περιθώριο λέξεων",
    liveRepWindowHint: "Μέγιστη απόσταση λέξεων μεταξύ εμφανίσεων (1–250).",
    liveRepFrequencyLabel: "Συχνότητα",
    liveRepFrequencyHint: "Ελάχιστες εμφανίσεις για αναφορά (2–20).",
    liveRepetitionsHeader: "Επαναλήψεις λέξεων",
    liveNoRepetitions: "Δεν βρέθηκαν επαναλήψεις λέξεων στην τρέχουσα παράγραφο.",
    liveRepOccurrences: "{count} εμφανίσεις",
    liveRepSuggestions: "Δοκιμάστε:",
    liveRepForms: "Τύποι:",
    askAiForHelp: "Ζητήστε βοήθεια AI",
    aiSuggestionsLoading: "Λήψη προτάσεων…",
    aiSuggestionsError: "Αδυναμία λήψης προτάσεων.",
    aiSuggestionsLabel: "AI προτείνει:",
    aiQuotaRemaining: "{count} αιτήματα AI απομένουν σήμερα",
    aiQuotaExhausted: "Εξαντλήθηκε το ημερήσιο όριο AI",
    severityLow: "Χαμηλή",
    severityMedium: "Μεσαία",
    severityHigh: "Υψηλή",
    tabSuggestions: "Προτάσεις",
    suggestionsInfo:
      "Ελέγχει ολόκληρο το έγγραφο ή μόνο την επιλογή σας και σας καθοδηγεί σε κάθε προτεινόμενη διόρθωση μία προς μία, ώστε να αποδέχεστε, να παρακάμπτετε ή να εφαρμόζετε όλες τις περιπτώσεις ενός κανόνα πριν αλλάξει οτιδήποτε.",
    scanSuggestionsBtn: "Έλεγχος Εγγράφου",
    scanSelectionSuggestionsBtn: "Έλεγχος Επιλογής",
    promptSuggestions:
      "Κάντε κλικ στο 'Έλεγχος Εγγράφου' για να ελέγξετε τις προτεινόμενες διορθώσεις σε όλο το έγγραφο, ή επιλέξτε πρώτα κείμενο και κάντε κλικ στο 'Έλεγχος Επιλογής' για να ελέγξετε μόνο αυτό το τμήμα.",
    suggestionsHeader: "Αναθεώρηση Προτάσεων",
    suggestionsCounter: "Πρόταση {current} από {total}",
    acceptAllRuleBtn: "Εφαρμογή σε όλο τον κανόνα",
    noSuggestions: "Δεν βρέθηκαν προτάσεις. Το έγγραφο συμμορφώνεται με τους επιλεγμένους κανόνες.",
    reportFalsePositive: "Αναφορά ως σφάλμα",
    reportFalsePositiveTitle: "Αναφορά σφάλματος",
    reportFalsePositiveDescription:
      "Βοηθήστε μας να βελτιωθούμε αναφέροντας όταν ένας κανόνας άλλαξε λανθασμένα κάποιο κείμενο.",
    reportFalsePositiveFeedbackLabel: "Επιπλέον σχόλια (προαιρετικά)",
    reportFalsePositiveFeedbackPlaceholder:
      "Εξηγήστε τι δεν λειτούργησε καλά και πώς θα έπρεπε να το χειριστεί...",
    reportFalsePositiveSend: "Αποστολή αναφοράς",
    reportFalsePositiveSending: "Αποστολή...",
    reportFalsePositiveSuccess: "Η αναφορά στάλθηκε επιτυχώς. Σας ευχαριστούμε!",
    reportFalsePositiveError: "Αποτυχία αποστολής αναφοράς. Παρακαλώ δοκιμάστε ξανά.",
    reportFalsePositiveCancel: "Ακύρωση",
    recentlyChangedTitle: "Πρόσφατες προσθήκες ή αλλαγές",
    badgeNew: "Νέο",
    badgeUpdated: "Ενημερωμένο",
    recentSelectionsTitle: "Πρόσφατες επιλογές",
    recentSelectionLabel: "{count} κανόνες",
    managedSectionTitle: "Διαχειριζόμενα προφίλ",
    personalSectionTitle: "Τα προφίλ μου",
    managedBadge: "Διαχειριζόμενο",
    duplicatePreset: "Αντιγραφή στα προφίλ μου",
    duplicatingPreset: "Αντιγράφεται…",
    quotaExceededTitle: "Φτάσατε το όριο του προγράμματός σας",
    quotaExceededMessage: "Αναβαθμίστε για περισσότερα scans και διορθώσεις.",
    serviceForbiddenMessage:
      "Αυτή η λειτουργία απαιτεί ανώτερο πρόγραμμα. Αναβαθμίστε για να συνεχίσετε.",
    quotaExceededUpgrade: "Αναβάθμιση προγράμματος",
    karlName: "Karl",
    karlRole: "ο βοηθός επιμελητή",
    karlAsk: "Ρώτησε τον Karl",
    karlAsking: "Ο Karl σκέφτεται…",
    karlAskRule: "Ρώτησε τον Karl για αυτόν τον κανόνα ({count})",
    karlAskAll: "Ρώτησε τον Karl για όλα τα ευρήματα ({count})",
    karlBulkPanelLabel: "Η εκτίμηση του Karl",
    karlBulkTitle: "Η εκτίμηση του Karl για {scope}",
    karlBulkScopeAll: "όλα τα ευρήματα",
    karlBulkEstimating: "Ο Karl υπολογίζει πόσες βοήθειες θα χρειαστούν…",
    karlBulkEstimate:
      "Θα χρειαστούν {count} βοήθειες· σας απομένουν {today} σήμερα και {month} αυτόν τον μήνα.",
    karlBulkEstimateOnly: "Θα χρειαστούν {count} βοήθειες.",
    karlBulkEstimateUnlimited: "Θα χρειαστούν {count} βοήθειες. Το πλάνο σας δεν έχει όριο.",
    karlBulkEstimateMonthOnly:
      "Θα χρειαστούν {count} βοήθειες· σας απομένουν {month} αυτόν τον μήνα.",
    karlBulkCachedNote: "Για {count} ευρήματα υπάρχει ήδη απάντηση και δεν κοστίζουν βοήθειες.",
    karlBulkShortfall:
      "Χρειάζονται {count} βοήθειες αλλά απομένουν μόνο {maximum}. Ο Karl μπορεί να απαντήσει στα πρώτα {maximum} κατά σειρά ανάγνωσης.",
    karlBulkAnswerAll: "Ρώτησε τον Karl για {count} ευρήματα",
    karlBulkAnswerFirst: "Απάντησε στα πρώτα {count}",
    karlBulkShowAnswers: "Εμφάνιση απαντήσεων του Karl",
    karlBulkCancel: "Άκυρο",
    karlBulkAnswered: "{count} απαντήθηκαν",
    karlBulkCached: "{count} είχαν ήδη απάντηση",
    karlBulkUnanswered: "{count} χωρίς απάντηση (δεν απομένουν βοήθειες)",
    karlBulkFailed: "{count} απέτυχαν",
    karlBulkLimitStopped:
      "Ο Karl σταμάτησε στον αριθμό ευρημάτων που εγκρίνατε. Ρωτήστε ξανά για τα υπόλοιπα.",
    karlKeepBadge: "Διατήρηση «{word}» · {confidence}",
    karlChangeBadge: "Αλλαγή σε «{word}» · {confidence}",
    karlConfidenceSure: "Σίγουρος",
    karlConfidenceLikely: "Μάλλον",
    karlConfidenceUncertain: "Αβέβαιος",
    karlStyleChoice: "Συχνά είναι ζήτημα ύφους.",
    karlUnsupported: "Δεν έχω εκπαιδευτεί σε αυτόν τον κανόνα ακόμη.",
    karlUnavailable: "Ο Karl δεν μπορεί να απαντήσει τώρα.",
    karlAssistsUsed:
      "{used} χρησιμοποιήθηκαν σήμερα · απομένουν {today} σήμερα · {month} αυτόν τον μήνα",
    karlAssistsMonthOnly: "{used} χρησιμοποιήθηκαν αυτόν τον μήνα · απομένουν {month}",
    karlAssistsUnlimited: "Απεριόριστες βοήθειες",
    karlAcceptedWithKarl: "Αποδεκτό με Karl",
    karlUndo: "Αναίρεση",
    karlApplyHint:
      "Ο Karl είναι σίγουρος για τα ευρήματα παρακάτω. Μπορείτε να εφαρμόσετε τις Σίγουρες απαντήσεις του και να τα παραλείψετε.",
    karlApplyAction: "Εφαρμογή των Σίγουρων απαντήσεων του Karl ({count})",
    karlApplyConfirmLabel: "Επιβεβαίωση μαζικής εφαρμογής",
    karlApplyConfirm:
      "Θα επιλυθούν {findings} ευρήματα για τα οποία ο Karl είναι σίγουρος, από τα οποία {changes} με αλλαγή. Κάθε ένα μπορεί να αναιρεθεί.",
    karlApplyConfirmAction: "Εφαρμογή {count}",
    karlApplyCancel: "Άκυρο",
    karlOverviewTitle: "Τα αποτελέσματα του Karl",
    karlOverviewHint: "Τα μετρήματα αφορούν τα ευρήματα αυτού του ελέγχου.",
    karlOverviewClearFilter: "Εμφάνιση όλων",
    karlOverviewNoAnswers:
      "Ρωτήστε τον Karl για ένα εύρημα για να δείτε τα αποτελέσματά του ανά κανόνα και να φιλτράρετε τις περιπτώσεις εδώ.",
    karlOverviewRuleCount: "{answered} από {total} απαντήθηκαν",
    karlCountAnswered: "Απαντήθηκαν",
    karlCountKeep: "Διατήρηση",
    karlCountChange: "Αλλαγή",
    karlCountSure: "Σίγουρος",
    karlCountLikely: "Μάλλον",
    karlCountUncertain: "Αβέβαιος",
    karlFilterLabel: "Μόνο Μάλλον και Αβέβαιος",
    karlFilterHint: "Παραλείψτε τις περιπτώσεις που ο Karl είναι σίγουρος.",
    karlFilteredEmpty: "Καμία περίπτωση δεν ταιριάζει με αυτό το φίλτρο.",
    karlAppliedTitle: "Εφαρμοσμένα με Karl",
    karlAssistsExhaustedToday:
      "Ο Karl δεν μπορεί να απαντήσει τώρα: δεν απομένουν βοήθειες για σήμερα. Ανανεώνονται τα μεσάνυχτα (UTC).",
    karlAssistsExhaustedCycle:
      "Ο Karl δεν μπορεί να απαντήσει τώρα: δεν απομένουν βοήθειες για αυτόν τον μήνα. Ανανεώνονται στην αρχή του επόμενου.",
    karlAssistsExhausted: "Ο Karl δεν μπορεί να απαντήσει τώρα: δεν απομένουν βοήθειες.",
    karlDisclaimer:
      "Οι απαντήσεις του Karl είναι βοηθητικές, όχι εγγύηση. Η δική σας ανάγνωση είναι η τελική σφραγίδα ποιότητας.",
    karlConsentTitle: "Πριν ρωτήσετε τον Karl",
    karlConsentDisclaimer:
      "Ο Karl είναι εξειδικευμένο εργαλείο. Οι απαντήσεις του δεν αποτελούν εγγύηση — η δική σας ανάγνωση είναι η τελική σφραγίδα ποιότητας.",
    karlConsentPrivacyNote:
      "Ο Karl απαντά μέσω εξωτερικού μοντέλου. Αποστέλλονται μόνο οι προτάσεις των ευρημάτων που ρωτάτε, χωρίς ο πάροχος να κρατά αντίγραφό τους.",
    karlConsentPrivacyLink: "Διαβάστε την πολιτική απορρήτου",
    karlConsentAccept: "Αποδοχή και συνέχεια",
    karlConsentCancel: "Όχι τώρα",
    karlConsentFailed: "Δεν μπορέσαμε να αποθηκεύσουμε την αποδοχή σας. Δοκιμάστε ξανά.",
    karlConsentAccountRequired:
      "Ο Karl χρειάζεται συνδεδεμένο λογαριασμό για να καταγράψει την αποδοχή σας. Συνδεθείτε από την καρτέλα Λογαριασμός και δοκιμάστε ξανά.",
    karlConsentGoToAccount: "Μετάβαση στον Λογαριασμό",
  },
};

/** The flat set of translation keys (English is the canonical shape). */
export type TranslationStrings = (typeof translations)["en"];

export function getDict(lang: Lang): TranslationStrings {
  return translations[lang];
}

/** Replace {placeholder} tokens, mirroring the original String.replace usage. */
export function format(template: string, vars: Record<string, string | number>): string {
  return Object.keys(vars).reduce(
    (acc, key) => acc.replaceAll(`{${key}}`, String(vars[key])),
    template
  );
}
